import { type ColorScheme, CreateOrUpdateTimeLogDeviceParams, type TimeLogDeviceDTO } from "@pentacode/core";
import { css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "../../../components/color-scheme/scheme-select";
import type { ColorSchemeSelect } from "../../../components/color-scheme/scheme-select";
import { alert } from "../../../elements/alert-dialog";
import { Checkbox } from "../../../elements/checkbox";
import { Dialog } from "../../../elements/dialog";
import "../../../elements/entity-filters";
import type { EntityFiltersEl } from "../../../elements/entity-filters";
import "../../../elements/help";
import { app } from "../../../init";

type DialogTimeLogDevice = Omit<TimeLogDeviceDTO, "id" | "companyId"> & {
    id?: TimeLogDeviceDTO["id"];
    companyId?: TimeLogDeviceDTO["companyId"];
};

@customElement("ptc-time-log-device-dialog")
export class TimeLogDeviceDialog extends Dialog<DialogTimeLogDevice, DialogTimeLogDevice> {
    @state()
    private _device: DialogTimeLogDevice;

    @query("form")
    private _form: HTMLFormElement;

    @query("input[name='description']")
    private _descriptionInput: HTMLInputElement;

    @query("ptc-checkbox-button[name='displaySchedule']")
    private _displayScheduleCheckbox: Checkbox;

    @query("ptc-checkbox-button[name='displayUpcoming']")
    private _displayUpcomingCheckbox: Checkbox;

    @query("ptc-checkbox-button[name='displayActive']")
    private _displayActiveCheckbox: Checkbox;

    @query("ptc-entity-filters")
    private _filtersInput: EntityFiltersEl;

    @query("ptc-color-scheme-select")
    private _colorSchemeSelect: ColorSchemeSelect;

    async show(device?: TimeLogDeviceDTO) {
        this._device = device ?? this._createNewDevice();
        const promise = super.show();

        await this.updateComplete;

        this._descriptionInput.value = this._device.description;
        this._filtersInput.filters = this._device.filters;
        this._displayActiveCheckbox.checked = this._device.displayActive;
        this._displayUpcomingCheckbox.checked = this._device.displayUpcoming;
        this._displayScheduleCheckbox.checked = this._device.displaySchedule;

        if (!this._descriptionInput.value) {
            this._descriptionInput.focus();
        }

        return promise;
    }

    private _createNewDevice(): DialogTimeLogDevice {
        return {
            appVersion: null,
            colorScheme: "system",
            description: "",
            connectionCode: "",
            displayActive: true,
            displaySchedule: true,
            displayUpcoming: true,
            filters: [],
            legacy: false,
        };
    }

    private async _submit(e: Event) {
        e.preventDefault();

        if (!this._form) {
            return;
        }

        const data = new FormData(this._form);

        const description = data.get("description") as string;
        const filters = this._filtersInput.filters;
        const displaySchedule = data.has("displaySchedule");
        const displayUpcoming = data.has("displayUpcoming");
        const displayActive = data.has("displayActive");
        // never renders the experimental option (no `experimental` flag), so the value is a persistable ColorScheme
        const colorScheme = this._colorSchemeSelect.value as ColorScheme;

        this.loading = true;

        try {
            const device = await app.api.createOrUpdateTimeLogDevice(
                new CreateOrUpdateTimeLogDeviceParams({
                    id: this._device.id,
                    description,
                    filters,
                    displaySchedule,
                    displayUpcoming,
                    displayActive,
                    colorScheme,
                })
            );

            this.done(device);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this.loading = false;
    }

    static styles = [...Dialog.styles, Checkbox.styles, css``];

    renderContent() {
        const isEdit = Boolean(this._device.id);

        return html`
            <form @submit=${this._submit} autocomplete="off" class="double-padded spacing vertical layout">
                <div class="small margined subtle blue colored-text">
                    ${isEdit
                        ? html`<ptc-ui-icon name="icon.action.edit"></ptc-ui-icon> Stempeluhr Bearbeiten`
                        : html`<ptc-ui-icon name="icon.action.add"></ptc-ui-icon> Neue Stempeluhr`}
                </div>

                ${isEdit
                    ? html`<div class="field">
                              <label>Verbindungscode</label>
                              <input
                                  value=${`${this._device.companyId}-${this._device.connectionCode}`}
                                  readonly
                                  disabled
                                  class="fill-horizontally"
                              />
                          </div>
                          <div class="small margined subtle orange colored-text">
                              Achtung! Verwenden Sie die Stempeluhr nur für ein Endgerät.
                          </div>`
                    : nothing}

                <div class="field">
                    <label>Name</label>
                    <input name="description" required placeholder="Bezeichnung" class="fill-horizontally" />
                </div>

                <div class="field" ?disabled=${this._device.legacy}>
                    <label>Arbeitsbereiche</label>
                    <ptc-entity-filters
                        class="focus-container"
                        .filterTypes=${["venue", "department", "position"] as const}
                    ></ptc-entity-filters>
                </div>

                <div class="field" ?disabled=${this._device.legacy}>
                    <label>
                        Funktionen

                        <ptc-help-anchor page="handbuch/zeiterfassung/#funktionen">
                            <ptc-ui-icon name="icon.navigation.documentation" class="faded"></ptc-ui-icon>
                        </ptc-help-anchor>
                    </label>
                    <div class="vertical spacing layout">
                        <ptc-checkbox-button buttonClass="slim ghost" label="Dienstplan" name="displaySchedule">
                        </ptc-checkbox-button>
                        <ptc-checkbox-button
                            buttonClass="slim ghost"
                            label="Ausstehende Schichten"
                            name="displayUpcoming"
                        ></ptc-checkbox-button>
                        <ptc-checkbox-button
                            buttonClass="slim ghost"
                            label="Aktive Schichten"
                            name="displayActive"
                        ></ptc-checkbox-button>
                    </div>
                </div>

                <div class="field" ?disabled=${this._device.legacy}>
                    <label> Farbschema </label>
                    <div class="vertical spacing layout">
                        <ptc-color-scheme-select .value=${this._device.colorScheme}> </ptc-color-scheme-select>
                    </div>
                </div>

                <div class="horizontal top-padded spacing evenly stretching layout">
                    <button class="primary">Speichern</button>
                    <button class="transparent" type="button" @click=${() => this.done()}>Abbrechen</button>
                </div>
            </form>
        `;
    }
}
